import { useState } from "react";

import {
  TextField,
  IconButton,
  InputAdornment,
  Typography,
  Box,
  Stack,
  LinearProgress,
} from "@mui/material";

import {
  VisibilityOutlined,
  VisibilityOffOutlined,
  CheckCircleOutline,
  RadioButtonUnchecked,
} from "@mui/icons-material";

import { Col } from "react-bootstrap";

export default function PasswordFields({
  values,
  handleChange,
  errors,
  touched,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);

  /*
   * ============================================================
   * WYMAGANIA HASŁA
   * ============================================================
   */

  const passwordChecks = {
    length: values.password?.length >= 8,
    number: /[0-9]/.test(values.password || ""),
    special: /[^a-zA-Z0-9]/.test(values.password || ""),
  };

  const completedChecks = Object.values(passwordChecks).filter(Boolean).length;

  /*
   * Siła hasła:
   * 0 = brak
   * 33 = jedno wymaganie
   * 66 = dwa wymagania
   * 100 = wszystkie wymagania
   */

  const passwordStrength = (completedChecks / 3) * 100;

  const passwordsMatch =
    values.passwordrepeat?.length > 0 &&
    values.password === values.passwordrepeat;

  /*
   * ============================================================
   * OPIS SIŁY HASŁA
   * ============================================================
   */

  const getStrengthLabel = () => {
    if (!values.password) {
      return "";
    }

    if (completedChecks === 1) {
      return "Słabe hasło";
    }

    if (completedChecks === 2) {
      return "Dobre hasło";
    }

    if (completedChecks === 3) {
      return "Bezpieczne hasło";
    }

    return "Hasło nie spełnia wymagań";
  };

  /*
   * ============================================================
   * STYLE
   * ============================================================
   */

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      backgroundColor: "#fff",
    },
  };

  /*
   * ============================================================
   * POJEDYNCZE WYMAGANIE
   * ============================================================
   */

  const Requirement = ({ completed, children }) => (
    <Stack direction="row" spacing={0.7} alignItems="center">
      {completed ? (
        <CheckCircleOutline
          sx={{
            fontSize: 17,
            color: "#2e7d32",
          }}
        />
      ) : (
        <RadioButtonUnchecked
          sx={{
            fontSize: 17,
            color: "#aaa",
          }}
        />
      )}

      <Typography
        variant="body2"
        sx={{
          fontSize: "0.82rem",
          color: completed ? "#2e7d32" : "text.secondary",
          fontWeight: completed ? 600 : 400,
        }}
      >
        {children}
      </Typography>
    </Stack>
  );

  /*
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <>
      {/* =======================================================
          HASŁO
      ======================================================= */}

      <Col xs={12} md={6}>
        <TextField
          fullWidth
          label="Hasło"
          type={showPassword ? "text" : "password"}
          name="password"
          margin="normal"
          autoComplete="new-password"
          value={values.password}
          onChange={handleChange}
          error={touched.password && Boolean(errors.password)}
          helperText={touched.password && errors.password}
          sx={fieldSx}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  type="button"
                  aria-label={showPassword ? "Ukryj hasło" : "Pokaż hasło"}
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                >
                  {showPassword ? (
                    <VisibilityOffOutlined />
                  ) : (
                    <VisibilityOutlined />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* =====================================================
            SIŁA HASŁA
        ===================================================== */}

        {values.password && (
          <Box
            sx={{
              mt: 1,
              px: 0.5,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 0.7,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: "0.78rem",
                }}
              >
                Siła hasła
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: completedChecks === 3 ? "#2e7d32" : "text.secondary",
                }}
              >
                {getStrengthLabel()}
              </Typography>
            </Stack>

            <LinearProgress
              variant="determinate"
              value={passwordStrength}
              sx={{
                height: 5,
                borderRadius: 10,

                "& .MuiLinearProgress-bar": {
                  borderRadius: 10,
                },
              }}
            />
          </Box>
        )}

        {/* =====================================================
            WYMAGANIA
        ===================================================== */}

        <Box
          sx={{
            mt: 1.5,
            px: 0.5,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: "0.78rem",
              mb: 0.8,
            }}
          >
            Hasło powinno zawierać:
          </Typography>

          <Stack spacing={0.6}>
            <Requirement completed={passwordChecks.length}>
              minimum 8 znaków
            </Requirement>

            <Requirement completed={passwordChecks.number}>
              co najmniej jedną cyfrę
            </Requirement>

            <Requirement completed={passwordChecks.special}>
              co najmniej jeden znak specjalny, np. ! @ # $
            </Requirement>
          </Stack>
        </Box>
      </Col>

      {/* =======================================================
          POWTÓRZENIE HASŁA
      ======================================================= */}

      <Col xs={12} md={6}>
        <TextField
          fullWidth
          label="Powtórz hasło"
          type={showPasswordRepeat ? "text" : "password"}
          name="passwordrepeat"
          margin="normal"
          autoComplete="new-password"
          value={values.passwordrepeat}
          onChange={handleChange}
          error={touched.passwordrepeat && Boolean(errors.passwordrepeat)}
          helperText={touched.passwordrepeat && errors.passwordrepeat}
          sx={fieldSx}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  type="button"
                  aria-label={
                    showPasswordRepeat ? "Ukryj hasło" : "Pokaż hasło"
                  }
                  onClick={() => setShowPasswordRepeat((prev) => !prev)}
                  edge="end"
                >
                  {showPasswordRepeat ? (
                    <VisibilityOffOutlined />
                  ) : (
                    <VisibilityOutlined />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* =====================================================
            ZGODNOŚĆ HASEŁ
        ===================================================== */}

        {values.passwordrepeat && (
          <Box
            sx={{
              mt: 1,
              px: 0.5,
            }}
          >
            {passwordsMatch ? (
              <Stack direction="row" spacing={0.7} alignItems="center">
                <CheckCircleOutline
                  sx={{
                    fontSize: 18,
                    color: "#2e7d32",
                  }}
                />

                <Typography
                  variant="body2"
                  sx={{
                    color: "#2e7d32",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                  }}
                >
                  Hasła są identyczne
                </Typography>
              </Stack>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: "0.82rem",
                }}
              >
                Powtórzone hasło musi być identyczne z pierwszym.
              </Typography>
            )}
          </Box>
        )}
      </Col>
    </>
  );
}
